<script lang="ts" setup>
import { useDebounceFn } from '@vueuse/core';
import { ArrowRight, ArrowUpRight, FileText } from 'lucide-vue-next';
import { generateProjectApi, getSuggestions as getSuggestionsApi } from '~/api/projects';
import type { EntityType, Suggestion } from '~/api/types';
import VButton from '~/components/VButton.vue';
import VInput from '~/components/VInput.vue';
import { entities } from '~/enitityHelper';
import { useAnalyticsStore } from '~/stores/AnalyticsStore';
import { useAuthStore } from '~/stores/AuthStore';
import { useUserStore } from '~/stores/UserStore';

// Constants
const MIN_PROMPT_LENGTH = 2;
const MAX_PROMPT_LENGTH = 255;

const writeIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <path d="M4.7774 17.5L6.07202 16.1166H17.6861C17.8675 16.1166 18.0207 16.1867 18.1458 16.3271C18.2709 16.4607 18.3334 16.6211 18.3334 16.8083C18.3334 16.9954 18.2709 17.1558 18.1458 17.2895C18.0207 17.4298 17.8675 17.5 17.6861 17.5H4.7774ZM4.00813 16.6178L2.01929 17.4699C1.91297 17.5167 1.81915 17.4933 1.73785 17.3998C1.6628 17.3062 1.64716 17.2059 1.69094 17.099L2.54464 15.0439L12.3669 4.5678L13.8304 6.13169L4.00813 16.6178ZM14.5809 5.34974L13.0986 3.77582L13.9523 2.86355C14.1712 2.63632 14.3901 2.51602 14.609 2.50265C14.8342 2.4826 15.0437 2.57617 15.2376 2.78335L15.5003 3.06405C15.7004 3.27792 15.7942 3.50181 15.7817 3.73572C15.7692 3.96964 15.6535 4.2069 15.4346 4.4475L14.5809 5.34974Z" fill="black"/>
</svg>`;

// Props and stores
const props = withDefaults(defineProps<{ entity: EntityType }>(), { entity: 'project' });
const level = ref(props.entity);
const entityMeta = entities[props.entity];

const analyticsStore = useAnalyticsStore();
const authStore = useAuthStore();
const userStore = useUserStore();

const cookie = useCookie('query', { maxAge: 60 * 3, default: () => '' });
const input = ref(cookie.value);
const willGenerateAfterLogin = useCookie<boolean>('willGenerateAfterLogin', { maxAge: 60, default: () => false });
const btnDisabled = ref(false);
const suggestions = ref<Suggestion[]>([]);
const isSuggestionsOpen = ref(false);
const suggestionListRef = ref<HTMLUListElement>();
const formContainerRef = ref<HTMLDivElement>();
const currentFocusIndex = ref(-1);

const sendYandexAnalyticsOnGenerate = () => {
  if (window && window.ym) window.ym(95948387, 'reachGoal', 'GENERATE');
  if (window && window._tmr) window._tmr.push({ type: 'reachGoal', id: '3558309', goal: 'generate_project' });
  if (window && window.dataLayer) window.dataLayer.push({ event: 'GenerateProject' });
};

const getSuggestions = async () => {
  try {
    const newSuggestions = await getSuggestionsApi(input.value, props.entity);
    currentFocusIndex.value = -1;
    suggestions.value = newSuggestions;
  } catch {
    suggestions.value = [];
  }
};

const debouncedGetSuggestions = useDebounceFn(getSuggestions, 300);

const handleKeyNavigation = (e: KeyboardEvent) => {
  if (!suggestions.value.length) return;

  if (e.key === 'ArrowDown') {
    e.preventDefault();
    if (currentFocusIndex.value < suggestions.value.length - 1) currentFocusIndex.value++;
    else currentFocusIndex.value = 0;

    (suggestionListRef.value?.children[currentFocusIndex.value]?.children[0] as HTMLAnchorElement)?.focus();
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    if (currentFocusIndex.value > 0) currentFocusIndex.value--;
    else currentFocusIndex.value = suggestions.value.length - 1;

    (suggestionListRef.value?.children[currentFocusIndex.value]?.children[0] as HTMLAnchorElement)?.focus();
  }
};

async function generateProject(prompt: string, level: string) {
  isSuggestionsOpen.value = false;
  if (MIN_PROMPT_LENGTH <= prompt.length) {
    analyticsStore.submitPrompt(prompt.slice(0, MAX_PROMPT_LENGTH));
    if (userStore.user) {
      btnDisabled.value = true;
      const p = await generateProjectApi(prompt.slice(0, MAX_PROMPT_LENGTH), level, props.entity);
      sendYandexAnalyticsOnGenerate();
      await navigateTo(`/projects/${p.id}`);
      btnDisabled.value = false;
    } else {
      willGenerateAfterLogin.value = true;
      authStore.showAuth = true;
    }
  }
}

const formAction = (event: Event) => {
  event.preventDefault();
  generateProject(input.value, level.value);
};

watch(input, () => {
  cookie.value = input.value || '';
  analyticsStore.enterPrompt(input.value);
  debouncedGetSuggestions();
});

watch(isSuggestionsOpen, () => {
  currentFocusIndex.value = -1;
});

watch(
  () => userStore.user,
  (userValue) => {
    if (willGenerateAfterLogin.value && userValue && input.value?.length >= MIN_PROMPT_LENGTH && level.value) {
      willGenerateAfterLogin.value = false;
      return generateProject(input.value, level.value);
    }
  },
  { immediate: true }
);

onMounted(() => {
  if (input.value?.length) getSuggestions();
});
</script>

<template>
  <div
    ref="formContainerRef"
    class="form-container"
    v-bind="$attrs"
    :class="{ open: isSuggestionsOpen }"
  >
    <transition name="fade-in">
      <div
        v-if="isSuggestionsOpen"
        class="overlay"
        @click="isSuggestionsOpen = false"
      />
    </transition>
    <form
      @submit.prevent="formAction"
      class="form"
      :class="{ open: isSuggestionsOpen }"
      @blur="isSuggestionsOpen = false"
    >
      <v-input
        v-model="input"
        :disabled="btnDisabled"
        :icon-html-left="writeIcon"
        :placeholder="entityMeta.createProjectBlock.inputPlaceholder"
        autofocus
        class="create-input"
        schema="white"
        enterkeyhint="done"
        :button-right="true"
        @keydown.enter="generateProject(input, level)"
        @keydown.stop="handleKeyNavigation"
        @focus="isSuggestionsOpen = true"
        @click="isSuggestionsOpen = true"
      >
        <template
          v-if="isSuggestionsOpen"
          #buttonRight
        >
          <v-button
            schema="primary"
            class="create-button-mobile"
            style="border-radius: 12px; padding: 12px"
            @click="generateProject(input, level)"
          >
            <ArrowRight />
          </v-button>
        </template>
      </v-input>
      <transition name="slide-down">
        <ul
          v-if="isSuggestionsOpen && suggestions.length"
          ref="suggestionListRef"
          class="suggestions"
        >
          <li
            v-for="(suggestion, index) in suggestions"
            :key="index + suggestion.project_id + input"
          >
            <NuxtLink
              :to="`/projects/${suggestion.project_id}`"
              class="suggestion"
              @click="analyticsStore.acceptSuggestion(+suggestion.project_id, input)"
              @keydown.stop="handleKeyNavigation"
            >
              <FileText class="suggestion-icon" />
              <span class="suggestion-title">
                {{ suggestion.title }}
              </span>
              <ArrowUpRight
                class="suggestion-icon"
                style="margin-left: auto; opacity: 0.3; width: 24px; height: 24px"
              />
            </NuxtLink>
          </li>
        </ul>
      </transition>
      <v-button
        :class="{ open: isSuggestionsOpen }"
        :disabled="btnDisabled"
        :loading="btnDisabled"
        class="create-button"
        schema="primary"
        @click="generateProject(input, level)"
      >
        Создать
        <template #iconLeft>
          <NuxtImg
            alt="Sparkles"
            height="20"
            src="/images/icons/sparkles.svg"
            width="20"
          />
        </template>
      </v-button>
    </form>
  </div>
</template>

<style lang="scss" scoped>
.fade-in-enter-active,
.fade-in-leave-active {
  transition: opacity 150ms ease-in;
}

.fade-in-enter-from,
.fade-in-leave-to {
  opacity: 0;
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition:
    transform 250ms ease-out,
    opacity 50ms ease-in;
}

.slide-down-enter-from,
.slide-down-leave-to {
  transform: translateY(-40px);
  opacity: 0;
}

.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);

  @include media-breakpoint-down(md) {
    // background: $background-theme-fade;
    background: rgba(0, 0, 0, 0.1);
  }
}
.form-container {
  position: relative;
  border-radius: 12px;
  z-index: 100;

  margin: auto;

  @include media-breakpoint-down(md) {
    width: 100%;
    padding: 0 20px;
    height: 100%;
    transition: padding 150ms ease-in-out;

    &.open {
      padding: 0 8px;
    }
  }

  .form {
    flex-direction: row;
    padding: 8px;
    width: calc(100% + 16px);
    margin-left: -8px;
    border-radius: 16px;
    justify-content: center;
    gap: 12px;
    display: grid;
    grid-template-columns: 10fr 2fr;
    z-index: 10;
    background: $background-theme-fade;

    @include media-breakpoint-down(md) {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      height: 100%;
    }

    .suggestions {
      position: absolute;
      top: calc(100% + 12px);
      left: 0;
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 2px;
      background: $background-white;
      border-radius: 12px;
      padding: 16px;
      z-index: 10;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

      @include media-breakpoint-down(md) {
        top: calc(100%);
        left: 8px;
        width: calc(100% - 16px);
        z-index: 10;
      }

      .suggestion {
        padding: 8px;
        border-radius: 12px;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 8px;
        color: $foreground-contrast;

        .suggestion-icon {
          width: 20px;
          height: 20px;
        }

        .suggestion-title {
          font-size: 16px;
          font-weight: 500;
          line-height: 22px;
          line-clamp: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        &:hover,
        &:focus {
          background: $background-gray;
          outline: none;
        }
      }
    }
  }

  .create-input {
    position: relative;
    flex: 1;
    border-radius: 12px;
    > div > div > input {
      background: white !important;
    }

    .create-button-mobile {
      display: none;

      @include media-breakpoint-down(md) {
        display: flex;
      }

      position: absolute;
      bottom: 50%;
      right: 4px;
      transform: translateY(50%);
      height: calc(100% - 8px);
      border-radius: 0;
    }
  }

  .create-button {
    z-index: 10;
    transition: opacity 150ms ease-in-out;

    @include media-breakpoint-down(md) {
      &.open {
        opacity: 0;
        display: none;
      }
    }
  }
}
</style>
